import { useState, useEffect, useRef } from 'react';
import { useLocation, useParams } from 'react-router-dom';
import { Search, Plus, Upload, Trash2, X, RefreshCw, Shield, Settings2, Hash, Keyboard } from 'lucide-react';
import { Button } from '../../ui/button';
import { Input } from '../../ui/input';
import { Label } from '../../ui/label';
import { Switch } from '../../ui/switch';
import { Textarea } from '../../ui/textarea';
import { Checkbox } from '../../ui/checkbox';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '../../ui/dialog';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../ui/tabs';
import { useToast } from '../../../hooks/use-toast';
import { myLocalStorage } from '../../StorageHelper';
import { WhitelistDetails, WhitelistHash } from './interface';
import { deleteHashes, submitHashes, toggleEnableForAgentUpdate, toggleEnableForWhiteList, whiteListingDetails } from './Api/policyprofileapi';
import { formatDate } from '../formatTimeAgo';
import CardPagination from '../CardPagination';
import EndpointActionButton from '../Endpoints/EndpointActionButton';
import PolicyProfileHeader from './PolicyProfileHeader';
import '../ManagementDialog.css';



export default function PolicyProfilesSettings() {
    const location = useLocation();
    const { osType: osTypeParam } = useParams();
    const profileName = location.state?.profileName || localStorage.getItem('currentProfileName') || '';
    const [profile, setProfile] = useState(() => myLocalStorage.getItem("profilesDetails"));

    const osType = osTypeParam || 'windows';
    const { toast } = useToast();
    const [activeSection, setActiveSection] = useState('agent-auto-update');

    const [agentAutoUpdate, setAgentAutoUpdate] = useState<boolean>(false);
    const [sha256WhitelistingEnabled, setSha256WhitelistingEnabled] = useState<boolean>(false);
    const [whitelistHashes, setWhitelistHashes] = useState<WhitelistDetails>();

    const [showAddHashDialog, setShowAddHashDialog] = useState(false);
    const [hashDialogTab, setHashDialogTab] = useState('manual');
    const [newHashInput, setNewHashInput] = useState('');
    const [pendingHashes, setPendingHashes] = useState<string[]>([]);
    const [selectedHashes, setSelectedHashes] = useState<string[]>([]);
    const [hashSearchTerm, setHashSearchTerm] = useState('');
    const [isDragOver, setIsDragOver] = useState(false);
    const [settingsSearchTerm, setSettingsSearchTerm] = useState('');
    const [showDeleteDialog, setShowDeleteDialog] = useState(false);
    const fileInputRef = useRef<HTMLInputElement>(null);

    // Pagination
    const [currentPage, setCurrentPage] = useState(1);
    const itemsPerPage = 10;

    const filteredHashes: WhitelistHash[] = (whitelistHashes?.whitelistHashes || []).filter(
        (hash) =>
            hash.hashValue.toLowerCase().includes(hashSearchTerm.toLowerCase()) ||
            hash.whitelistLabel?.toLowerCase().includes(hashSearchTerm.toLowerCase())
    );

    const totalPages = Math.ceil(filteredHashes.length / itemsPerPage);
    const startIndex = (currentPage - 1) * itemsPerPage;
    const paginatedHashes = filteredHashes.slice(startIndex, startIndex + itemsPerPage);

    const handleToggleSetting = async (
        setting: 'sha256WhitelistingEnabled' | 'agentAutoUpdate'
    ) => {
        if (setting === 'sha256WhitelistingEnabled') {
            const newValue = !sha256WhitelistingEnabled;
            setSha256WhitelistingEnabled(newValue); // immediate state change
            const updatedProfile = { ...profile, isWhitelistEnabled: newValue };
            setProfile(updatedProfile);
            myLocalStorage.setItem("profilesDetails", updatedProfile);
            try {
                await toggleEnableForWhiteList(profile.id, newValue);
            } catch (error) {
                // revert UI if API fails
                setSha256WhitelistingEnabled(!newValue);
                toast({
                    title: "Failed to update whitelist setting",
                    variant: "destructive",
                });
            }
        } else if (setting === 'agentAutoUpdate') {
            const newValue = !agentAutoUpdate;
            setAgentAutoUpdate(newValue);
            const updatedProfile = { ...profile, autoAgentUpgradeEnabled: newValue };
            setProfile(updatedProfile);
            myLocalStorage.setItem("profilesDetails", updatedProfile);
            try {
                await toggleEnableForAgentUpdate(profile.id, newValue);
            } catch (error) {
                setAgentAutoUpdate(!newValue);
                toast({
                    title: "Failed to update agent auto-update setting",
                    variant: "destructive",
                });
            }
        }
    };



    const handleAddHash = () => {
        const hashValue = newHashInput.trim();

        if (!hashValue) return;

        // Basic SHA-256 validation (64 hex characters)
        if (!/^[a-fA-F0-9]{64}$/.test(hashValue)) {
            toast({
                title: "Invalid hash format",
                description: "Please enter a valid SHA-256 hash (64 hex characters)",
                variant: "destructive",
            });
            return;
        }

        // Check for duplicates (case-insensitive)
        const isDuplicate = pendingHashes.some(
            (hash) => hash.toLowerCase() === hashValue.toLowerCase()
        );

        if (isDuplicate) {
            toast({
                title: "Duplicate hash",
                description: "This hash is already in the list.",
                variant: "destructive",
            });
            return;
        }

        setPendingHashes((prev) => [...prev, hashValue]);
        setNewHashInput("");
    };


    const handleRemovePendingHash = (hashToRemove: string) => {
        setPendingHashes(prev => prev.filter(hash => hash !== hashToRemove));
    };

    const handleSubmitHashes = async () => {
        if (pendingHashes.length === 0) return;

        const currentDate = new Date().toISOString().split("T")[0]; // YYYY-MM-DD format

        const payload = {
            profileId: profile.id,
            isWhitelistEnabled: sha256WhitelistingEnabled,
            whitelistHashes: pendingHashes.map((hash) => ({
                hash,
                label: currentDate,
            })),
        };

        try {
            await submitHashes(payload);
            toast({
                title: "Hashes submitted successfully",
                description: `${pendingHashes.length} hash${pendingHashes.length > 1 ? "es" : ""} added to whitelist.`,
            });
            setPendingHashes([]);
            setShowAddHashDialog(false);
        } catch (error) {
            toast({
                title: "Submission failed",
                description: "An error occurred while submitting hashes.",
                variant: "destructive",
            });
        }
        getWhiteListingDetails()
    };


    const handleSelectHash = (hashValue: string, checked: boolean) => {
        if (checked) {
            setSelectedHashes(prev => [...prev, hashValue]);
        } else {
            setSelectedHashes(prev => prev.filter(id => id !== hashValue));
        }
    };

    const handleDeleteSelected = () => {
        if (selectedHashes.length === 0) return;
        setShowDeleteDialog(true);
    };

    const confirmDelete = async () => {
        if (selectedHashes.length === 0) return;

        const uniqueHashes = Array.from(
            new Set(selectedHashes.map((hash) => hash.toLowerCase()))
        );

        const payload = {
            hashList: uniqueHashes,
        };

        try {
            await deleteHashes(payload, profile.id);

            const count = uniqueHashes.length;
            setSelectedHashes([]);
            setShowDeleteDialog(false);

            toast({
                title: `${count} hash${count > 1 ? "es" : ""} deleted successfully`,
            });
        } catch (error) {
            toast({
                title: "Deletion failed",
                description: "An error occurred while deleting hashes.",
                variant: "destructive",
            });
        }
    };


    const processFile = (file: File) => {
        const reader = new FileReader();
        reader.onload = (e) => {
            const content = e.target?.result as string;
            const hashes = content.split('\n')
                .map(line => line.trim())
                .filter(line => line && /^[a-fA-F0-9]{64}$/.test(line));

            setPendingHashes(prev => [...prev, ...hashes]);
            toast({ title: `${hashes.length} hash(es) loaded from file` });
        };
        reader.readAsText(file);
    };

    const handleFileUpload = (event: React.ChangeEvent<HTMLInputElement>) => {
        const file = event.target.files?.[0];
        if (!file) return;
        processFile(file);
        // Reset the input
        event.target.value = '';
    };

    const handleDragOver = (e: React.DragEvent) => {
        e.preventDefault();
        setIsDragOver(true);
    };

    const handleDragLeave = (e: React.DragEvent) => {
        e.preventDefault();
        setIsDragOver(false);
    };

    const handleDrop = (e: React.DragEvent) => {
        e.preventDefault();
        setIsDragOver(false);

        const files = Array.from(e.dataTransfer.files);
        const file = files[0];

        if (file && (file.type === 'text/plain' || file.name.endsWith('.txt') || file.name.endsWith('.csv'))) {
            processFile(file);
        } else {
            toast({ title: "Invalid file type", description: "Please upload a text (.txt) or CSV (.csv) file" });
        }
    };

    const sidebarItems = [
        { id: 'agent-auto-update', label: 'Agent Auto Update', icon: RefreshCw },
        { id: 'whitelisting-hashes', label: 'Whitelisting Hashes', icon: Shield },
    ];

    // Filter sidebar items based on OS type and search
    const filteredSidebarItems = sidebarItems
        .filter(item => {
            // Hide Whitelisting Hashes for Linux profiles
            if (item.id === 'whitelisting-hashes' && osType === 'linux') {
                return false;
            }
            return true;
        })
        .filter(item =>
            item.label.toLowerCase().includes(settingsSearchTerm.toLowerCase())
        );

    const getWhiteListingDetails = async () => {
        let response: WhitelistDetails = await whiteListingDetails(profile.id)
        setWhitelistHashes(response);
    }


    useEffect(() => {
        if (activeSection !== "agent-auto-update") {
            getWhiteListingDetails();
        }
    }, [activeSection])


    useEffect(() => {
        setAgentAutoUpdate(profile.autoAgentUpgradeEnabled)
        setSha256WhitelistingEnabled(profile.isWhitelistEnabled)
    }, [profile])

    return (
        <div className="endpoint-detail-page policy-detail-page custom-scrollbar flex h-full min-h-0 min-w-0 flex-1 flex-col overflow-x-hidden overflow-y-auto bg-background">
            <PolicyProfileHeader
                profileName={profileName}
                os={osType}
                section="Settings"
            />

            <div className="policy-settings-layout">
                <aside className="policy-card policy-settings-nav">
                    <div className="policy-settings-nav-head">
                        <span className="category-icon" aria-hidden="true">
                            <Settings2 />
                        </span>
                        <div className="min-w-0">
                            <h2 className="policy-settings-nav-title">Settings</h2>
                            <p className="policy-settings-nav-sub">{osType} profile settings</p>
                        </div>
                    </div>
                    <div className="policy-search policy-settings-search">
                        <Search aria-hidden="true" />
                        <Input
                            placeholder="Search"
                            aria-label="Search settings"
                            value={settingsSearchTerm}
                            onChange={(e) => setSettingsSearchTerm(e.target.value)}
                            data-testid="input-search-settings"
                        />
                    </div>
                    <div className="policy-settings-nav-list">
                        {filteredSidebarItems.map((item) => (
                            <Button
                                key={item.id}
                                variant={activeSection === item.id ? "default" : "ghost"}
                                className="policy-settings-nav-item no-default-hover-elevate"
                                aria-current={activeSection === item.id ? "true" : undefined}
                                onClick={() => setActiveSection(item.id)}
                                data-testid={`button-${item.id}`}
                            >
                                <item.icon />
                                {item.label}
                            </Button>
                        ))}
                        {filteredSidebarItems.length === 0 && (
                            <p className="policy-settings-nav-empty">No settings match your search.</p>
                        )}
                    </div>
                </aside>

                <div className="policy-settings-content">
                    {activeSection === 'agent-auto-update' && (
                        <section className="policy-card policy-setting-card">
                            <div className="policy-setting-head">
                                <div className="policy-panel-heading">
                                    <span className="category-icon" aria-hidden="true">
                                        <RefreshCw />
                                    </span>
                                    <div className="min-w-0">
                                        <h2 className="policy-panel-title">Agent Auto Update</h2>
                                        <p className="policy-panel-description">
                                            Configure automatic updates for the security agent on devices using this profile.
                                        </p>
                                    </div>
                                </div>
                            </div>
                            <div className="policy-setting-row">
                                <div className="min-w-0">
                                    <Label htmlFor="switch-agent-auto-update" className="policy-setting-label">Enable Auto Update</Label>
                                    <p className="policy-setting-hint">
                                        When enabled, agents will automatically update to the latest version.
                                    </p>
                                </div>
                                <Switch
                                    id="switch-agent-auto-update"
                                    checked={agentAutoUpdate}
                                    onCheckedChange={() => handleToggleSetting('agentAutoUpdate')}
                                    data-testid="switch-agent-auto-update"
                                />
                            </div>
                        </section>
                    )}

                    {activeSection === 'whitelisting-hashes' && (
                        <>
                            <section className="policy-card policy-setting-card">
                                <div className="policy-setting-head">
                                    <div className="policy-panel-heading">
                                        <span className="category-icon" data-tone="success" aria-hidden="true">
                                            <Shield />
                                        </span>
                                        <div className="min-w-0">
                                            <h2 className="policy-panel-title">SHA-256 Whitelisting Hashes</h2>
                                            <p className="policy-panel-description">
                                                Whitelisting controls the applications users can download and install.
                                                Only installers in whitelist hashes will be allowed to be installed by the user.
                                            </p>
                                        </div>
                                    </div>
                                    <div className="policy-setting-actions">
                                        <div className="policy-switch-field">
                                            <Label htmlFor="switch-sha256-whitelisting" className="policy-setting-hint">
                                                {sha256WhitelistingEnabled ? "Enabled" : "Disabled"}
                                            </Label>
                                            <Switch
                                                id="switch-sha256-whitelisting"
                                                checked={sha256WhitelistingEnabled}
                                                onCheckedChange={() => handleToggleSetting('sha256WhitelistingEnabled')}
                                                data-testid="switch-sha256-whitelisting"
                                            />
                                        </div>
                                        <EndpointActionButton
                                            onClick={() => setShowAddHashDialog(true)}
                                            disabled={!sha256WhitelistingEnabled}
                                            data-testid="button-add-hash"
                                        >
                                            <Plus className="w-4 h-4" />
                                            Add Hash
                                        </EndpointActionButton>
                                    </div>
                                </div>
                            </section>

                            {sha256WhitelistingEnabled && (
                                <section className="policy-card policy-hash-card">
                                    <div className="policy-hash-toolbar">
                                        <div className="policy-search">
                                            <Search aria-hidden="true" />
                                            <Input
                                                placeholder="Search Hash"
                                                aria-label="Search hashes"
                                                value={hashSearchTerm}
                                                onChange={(e) => {
                                                    setHashSearchTerm(e.target.value);
                                                    setCurrentPage(1);
                                                }}
                                                data-testid="input-search-hash"
                                            />
                                        </div>
                                        <Button
                                            variant="outline"
                                            className="policy-action policy-hash-delete no-default-hover-elevate"
                                            data-tone="danger"
                                            onClick={handleDeleteSelected}
                                            disabled={selectedHashes.length === 0}
                                            data-testid="button-delete-selected"
                                        >
                                            <Trash2 />
                                            Delete
                                        </Button>
                                    </div>

                                    {filteredHashes.length === 0 ? (
                                        <div className="policy-empty">
                                            <span className="policy-empty-icon" aria-hidden="true">
                                                <Hash />
                                            </span>
                                            <p>No hashes available</p>
                                        </div>
                                    ) : (
                                        <>
                                            <div className="policy-hash-scroll">
                                                <div className="policy-hash-table">
                                                    <div className="policy-table-head policy-hash-grid">
                                                        <div className="flex items-center">
                                                            <Checkbox
                                                                checked={selectedHashes.length === paginatedHashes.length && paginatedHashes.length > 0}
                                                                onCheckedChange={(checked) => {
                                                                    if (checked) {
                                                                        setSelectedHashes(paginatedHashes.map(h => h.hashValue));
                                                                    } else {
                                                                        setSelectedHashes([]);
                                                                    }
                                                                }}
                                                                aria-label="Select all hashes"
                                                                data-testid="checkbox-select-all"
                                                            />
                                                        </div>
                                                        <div>Hash value ({whitelistHashes?.whitelistHashes.length})</div>
                                                        <div>Hash type</div>
                                                        <div>Created At</div>
                                                    </div>

                                                    <div className="policy-hash-rows">
                                                        {paginatedHashes.map((hash) => (
                                                            <div key={hash.hashId} className="policy-hash-row policy-hash-grid">
                                                                <div className="flex items-center">
                                                                    <Checkbox
                                                                        checked={selectedHashes.includes(String(hash.hashId))}
                                                                        onCheckedChange={(checked) => handleSelectHash(hash.hashValue, checked as boolean)}
                                                                        aria-label={`Select hash ${hash.hashValue}`}
                                                                        data-testid={`checkbox-hash-${hash.hashId}`}
                                                                    />
                                                                </div>
                                                                <div className="min-w-0">
                                                                    <span className="policy-hash-value" data-testid={`text-hash-value-${hash.hashId}`}>
                                                                        {hash.hashValue}
                                                                    </span>
                                                                    {hash.whitelistLabel && (
                                                                        <span className="policy-hash-label">Label: {hash.whitelistLabel}</span>
                                                                    )}
                                                                </div>
                                                                <div>
                                                                    <span className="policy-badge">{hash.hashType}</span>
                                                                </div>
                                                                <div className="policy-hash-date">
                                                                    {formatDate(hash.createdAt)}
                                                                </div>
                                                            </div>
                                                        ))}
                                                    </div>
                                                </div>
                                            </div>

                                            <div className="policy-pagination">
                                                <span className="policy-pagination-summary">
                                                    Showing {startIndex + 1}–{Math.min(startIndex + itemsPerPage, filteredHashes.length)} of {filteredHashes.length} hashes
                                                </span>
                                                <CardPagination
                                                    label="Hash pages"
                                                    currentPage={currentPage}
                                                    totalPages={Math.max(1, totalPages)}
                                                    onPageChange={setCurrentPage}
                                                />
                                            </div>
                                        </>
                                    )}
                                </section>
                            )}
                        </>
                    )}
                </div>
            </div>

            <Dialog open={showAddHashDialog} onOpenChange={setShowAddHashDialog}>
                <DialogContent className="management-dialog policy-dialog sm:max-w-2xl">
                    <DialogHeader className="management-dialog-header">
                        <div className="management-dialog-heading">
                            <span className="management-dialog-icon">
                                <Hash />
                            </span>
                            <div>
                                <DialogTitle>Update Hashes</DialogTitle>
                                <DialogDescription>
                                    Add SHA-256 hashes manually or upload them from a file.
                                </DialogDescription>
                            </div>
                        </div>
                    </DialogHeader>

                    <div className="management-dialog-body">
                        <Tabs value={hashDialogTab} onValueChange={setHashDialogTab} className="policy-hash-tabs">
                            <TabsList unstyled className="cea-tabs policy-segmented">
                                <TabsTrigger
                                    unstyled
                                    value="manual"
                                    className={`cea-tab ${hashDialogTab === "manual" ? "active" : ""}`}
                                >
                                    <Keyboard />
                                    Update Manually
                                </TabsTrigger>
                                <TabsTrigger
                                    unstyled
                                    value="file"
                                    className={`cea-tab ${hashDialogTab === "file" ? "active" : ""}`}
                                >
                                    <Upload />
                                    File Upload
                                </TabsTrigger>
                            </TabsList>

                            <TabsContent value="manual" className="policy-hash-tab-panel mt-0">
                                <div className="management-form-field">
                                    <Label htmlFor="hash-input">Hash Value (Optional)</Label>
                                    <p className="policy-setting-hint">Enter SHA-256 hash value:</p>
                                    <Textarea
                                        id="hash-input"
                                        placeholder="Enter SHA-256 hash values, one per line..."
                                        value={newHashInput}
                                        onChange={(e) => setNewHashInput(e.target.value)}
                                        data-testid="textarea-hash-input"
                                    />
                                </div>
                                <Button
                                    variant="outline"
                                    onClick={handleAddHash}
                                    disabled={!newHashInput.trim()}
                                    className="policy-hash-add"
                                    data-testid="button-add-hash-manual"
                                >
                                    <Plus className="w-4 h-4" />
                                    Add Hash
                                </Button>
                            </TabsContent>

                            <TabsContent value="file" className="policy-hash-tab-panel mt-0">
                                <div
                                    className="policy-dropzone"
                                    data-active={isDragOver}
                                    onDragOver={handleDragOver}
                                    onDragLeave={handleDragLeave}
                                    onDrop={handleDrop}
                                >
                                    <span className="policy-dropzone-icon" aria-hidden="true">
                                        <Upload />
                                    </span>
                                    <p className="policy-dropzone-title">
                                        {isDragOver
                                            ? 'Drop your file here...'
                                            : 'Drag and drop a text file with SHA-256 hashes here'}
                                    </p>
                                    <p className="policy-setting-hint">Or</p>
                                    <input
                                        ref={fileInputRef}
                                        type="file"
                                        accept=".txt,.csv"
                                        onChange={handleFileUpload}
                                        className="hidden"
                                        data-testid="input-file-upload"
                                    />
                                    <Button
                                        variant="outline"
                                        onClick={() => fileInputRef.current?.click()}
                                        className="policy-hash-add"
                                        data-testid="button-select-file"
                                    >
                                        Select File
                                    </Button>
                                    <p className="policy-setting-hint">Supported formats: .txt, .csv</p>
                                </div>
                            </TabsContent>
                        </Tabs>

                        {pendingHashes.length > 0 && (
                            <div className="management-form-field">
                                <Label>Pending Hashes ({pendingHashes.length})</Label>
                                <div className="policy-pending-list">
                                    {pendingHashes.map((hash, index) => (
                                        <div key={index} className="policy-pending-item">
                                            <span className="policy-pending-value">{hash}</span>
                                            <Button
                                                variant="ghost"
                                                size="icon"
                                                onClick={() => handleRemovePendingHash(hash)}
                                                className="policy-pending-remove no-default-hover-elevate"
                                                aria-label="Remove hash"
                                                data-testid={`button-remove-hash-${index}`}
                                            >
                                                <X />
                                            </Button>
                                        </div>
                                    ))}
                                </div>
                            </div>
                        )}
                    </div>

                    <DialogFooter className="management-dialog-footer">
                        <Button
                            variant="outline"
                            onClick={() => {
                                setShowAddHashDialog(false);
                                setPendingHashes([]);
                                setNewHashInput('');
                            }}
                            data-testid="button-close-dialog"
                        >
                            Close
                        </Button>
                        <Button
                            onClick={handleSubmitHashes}
                            disabled={pendingHashes.length === 0}
                            className="endpoint-primary-action"
                            data-testid="button-submit-hashes"
                        >
                            Submit Hashes
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>

            <Dialog open={showDeleteDialog} onOpenChange={setShowDeleteDialog}>
                <DialogContent className="management-dialog policy-dialog sm:max-w-md">
                    <DialogHeader className="management-dialog-header">
                        <div className="management-dialog-heading">
                            <span className="management-dialog-icon" data-tone="danger">
                                <Trash2 />
                            </span>
                            <div>
                                <DialogTitle>Confirm Deletion</DialogTitle>
                                <DialogDescription>
                                    Are you sure you want to delete {selectedHashes.length} selected hash{selectedHashes.length !== 1 ? 'es' : ''}?
                                    This action cannot be undone.
                                </DialogDescription>
                            </div>
                        </div>
                    </DialogHeader>
                    <DialogFooter className="management-dialog-footer">
                        <Button
                            variant="outline"
                            onClick={() => setShowDeleteDialog(false)}
                            data-testid="button-cancel-delete"
                        >
                            Cancel
                        </Button>
                        <Button
                            variant="destructive"
                            onClick={confirmDelete}
                            data-testid="button-confirm-delete"
                        >
                            <Trash2 className="w-4 h-4" />
                            Delete {selectedHashes.length} Hash{selectedHashes.length !== 1 ? 'es' : ''}
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>
        </div>
    );
}
